import { useEffect } from 'react';
import {
  $getRoot,
  $getSelection,
  $insertNodes,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  LexicalEditor,
  LexicalNode,
  PASTE_COMMAND,
} from 'lexical';
import { DRAG_DROP_PASTE } from '@lexical/rich-text';
import { $generateNodesFromDOM } from '@lexical/html';
import { ExtendedTextNode } from '@/components/LexicalEditor/nodes/ExtendedTextNode';

export default function usePasteWithoutFontSizeAndColor(editor: LexicalEditor) {
  useEffect(() => {
    return editor.registerCommand<ClipboardEvent>(
      PASTE_COMMAND,
      (event) => {
        const files = event.clipboardData?.files;
        if (files && files.length > 0) {
          editor.dispatchCommand(DRAG_DROP_PASTE, Array.from(files));
          return true;
        }

        event.preventDefault();

        const html = event.clipboardData?.getData('text/html');
        const text = event.clipboardData?.getData('text/plain');
        if (!html && !text) return true;

        let nodes: LexicalNode[] = [];
        if (html) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          nodes = $generateNodesFromDOM(editor, doc) || [];
        } else if (text) {
          nodes = [new ExtendedTextNode(text)];
        }

        // font-size 및 color 제거 함수
        function removeFormattingRecursively(node: LexicalNode) {
          if ($isTextNode(node)) {
            const style = node.getStyle();
            if (style) {
              const newStyle = style
                .split(';')
                .filter((s) => {
                  const trimmed = s.trim();
                  return (
                    !trimmed.startsWith('font-size') &&
                    !trimmed.startsWith('color')
                  );
                })
                .join('; ');
              node.setStyle(newStyle);
            }
          }
          if ($isElementNode(node)) {
            node.getChildren?.().forEach(removeFormattingRecursively);
          }
        }

        nodes.forEach(removeFormattingRecursively);

        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            selection.insertNodes(nodes);
          } else {
            $getRoot().selectEnd();
            $insertNodes(nodes);
          }
        });

        return true; // 이벤트 처리 완료
      },
      1, // priority
    );
  }, [editor]);
}
